import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { t } from "@/translations"

/**
 * Sorts shipping methods by price (cheapest first)
 * @param shippingMethods Array of shipping methods
 * @returns Sorted array with cheapest methods first
 */
export const sortShippingMethodsByPrice = <
    T extends { priceAsNumber?: number; price?: string }
>(
    shippingMethods: T[]
): T[] => {
    return shippingMethods.sort((a, b) => {
        const priceA = a.priceAsNumber || 0
        const priceB = b.priceAsNumber || 0
        return priceA - priceB
    })
}

/**
 * True when the POC member free-shipping voucher is on the selection.
 *
 * Use this during checkout only — to apply free shipping to the cheapest eligible
 * method and for the "to be calculated" state before a shipping method is chosen.
 * Do not use this to label the final shipping price on confirmation; use
 * `getShippingMethodPriceLabel` with the receipt totals instead.
 */
export const hasFreeShippingVoucher = (vouchers: any[]): boolean => {
    return (
        vouchers?.some(
            (voucher: any) => voucher.voucher === FREE_SHIPPING_VOUCHER_CODE
        ) || false
    )
}

/**
 * Updates shipping methods to make the cheapest one free if free shipping voucher exists
 * @param shippingMethods Array of shipping methods (already sorted by price)
 * @param vouchers Array of vouchers from selection
 * @param langCode Language code for translations
 * @returns Updated shipping methods with free shipping applied
 */
export const applyFreeShippingToCheapestMethod = <
    T extends { priceAsNumber?: number; price?: string; [key: string]: any }
>(
    shippingMethods: T[],
    vouchers: any[],
    langCode: string
): T[] => {
    const hasFreeVoucher = hasFreeShippingVoucher(vouchers)

    if (!hasFreeVoucher || shippingMethods.length === 0) {
        return shippingMethods
    }

    // Find the free shipping voucher
    const freeShippingVoucher = vouchers.find(
        (voucher: any) => voucher.voucher === FREE_SHIPPING_VOUCHER_CODE
    )

    // Get the shipping method IDs that support free shipping
    const freeShippingForMethods = freeShippingVoucher?.freeShippingFor || []

    // Get all available shipping method IDs
    const availableShippingMethodIds = shippingMethods.map(
        method => method.shippingMethod
    )

    // Find which shipping methods are eligible for free shipping
    const eligibleShippingMethodIds = new Set(
        availableShippingMethodIds.filter(methodId =>
            freeShippingForMethods.includes(methodId)
        )
    )

    // If no methods are eligible, return original methods
    if (eligibleShippingMethodIds.size === 0) {
        return shippingMethods
    }

    // Find the first (cheapest) eligible method
    let cheapestEligibleMethodId: string | null = null
    for (const method of shippingMethods) {
        if (eligibleShippingMethodIds.has(method.shippingMethod)) {
            cheapestEligibleMethodId = method.shippingMethod
            break
        }
    }

    // If no eligible method found, return original methods
    if (!cheapestEligibleMethodId) {
        return shippingMethods
    }

    // Apply free shipping to the cheapest eligible method and sort so free shipping comes first
    const updatedMethods = shippingMethods.map((method: T) => {
        if (method.shippingMethod === cheapestEligibleMethodId) {
            return {
                ...method,
                isFree: true,
                price: t(langCode, "checkout.shipping.free"),
                priceAsNumber: 0
            }
        }
        return method
    })

    // Sort so free shipping method comes first, then by price
    return updatedMethods.sort((a: T, b: T) => {
        const aIsFree = a.isFree === true || a.priceAsNumber === 0
        const bIsFree = b.isFree === true || b.priceAsNumber === 0

        // Free shipping methods come first
        if (aIsFree && !bIsFree) return -1
        if (!aIsFree && bIsFree) return 1

        // If both are free or both are paid, sort by price
        const aPrice = (a as any).priceAsNumber ?? Infinity
        const bPrice = (b as any).priceAsNumber ?? Infinity
        return aPrice - bPrice
    })
}

/**
 * Label for a shipping method row or totals line — based on what was actually charged.
 */
export const getShippingMethodPriceLabel = (
    langCode: string,
    shippingPriceAsNumber: number,
    shippingPrice?: string
): string =>
    (shippingPriceAsNumber ?? 0) === 0
        ? t(langCode, "checkout.shipping.free")
        : (shippingPrice ?? "")
